import { useState, useRef, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  FileText, Receipt, FlaskConical, Scan,
  Upload, Trash2, Loader2, CheckCircle2, AlertCircle, ImageOff, UserCheck,
  Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, AlignRight,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { brandingApi } from '@/lib/api';
import { useBranding, Branding, PrintHeaderType } from '@/hooks/useBranding';
import { useAuthStore } from '@/store/auth.store';

// String-only keys of Branding — used where we expect a URL / text value, not a boolean
type StringBrandingKey = {
  [K in keyof Branding]: Branding[K] extends string ? K : never
}[keyof Branding];

// ── Document type config ───────────────────────────────────────────
interface DocConfig {
  type:        PrintHeaderType;
  label:       string;
  icon:        React.ElementType;
  description: string;
  brandingKey: StringBrandingKey;
}

const DOC_CONFIGS: DocConfig[] = [
  {
    type:        'prescription',
    label:       'Prescription Header',
    icon:        FileText,
    description: 'Shown at the top of blank prescriptions and OPD Rx prints.',
    brandingKey: 'print_header_prescription',
  },
  {
    type:        'invoice',
    label:       'Invoice Header',
    icon:        Receipt,
    description: 'Shown at the top of all billing invoices and receipts.',
    brandingKey: 'print_header_invoice',
  },
  {
    type:        'lab',
    label:       'Lab / Pathology Header',
    icon:        FlaskConical,
    description: 'Shown at the top of pathology and lab investigation reports.',
    brandingKey: 'print_header_lab',
  },
  {
    type:        'radiology',
    label:       'Radiology Header',
    icon:        Scan,
    description: 'Shown at the top of X-Ray, CT, MRI, and ultrasound reports.',
    brandingKey: 'print_header_radiology',
  },
];

// ── Per-card uploader component ────────────────────────────────────
function HeaderCard({
  config,
  currentUrl,
  onSaved,
}: {
  config:     DocConfig;
  currentUrl: string;
  onSaved:    () => void;
}) {
  const [preview,   setPreview]   = useState<string>(currentUrl);
  const [uploading, setUploading] = useState(false);
  const [removing,  setRemoving]  = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [error,     setError]     = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // Sync when branding data refreshes (e.g. after another card saves)
  useEffect(() => { setPreview(currentUrl); }, [currentUrl]);

  const Icon = config.icon;
  const hasImage = Boolean(preview);
  const isDirty  = preview !== currentUrl;

  function handleFile(file: File) {
    setError('');
    setSaved(false);
    const allowed = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('Only PNG, JPEG, SVG, or WebP images are allowed.');
      return;
    }
    if (file.size > 2_000_000) {
      setError('Image must be under 2 MB. Resize or compress it and try again.');
      return;
    }
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  async function handleUpload() {
    if (!preview || !isDirty) return;
    setUploading(true);
    setError('');
    try {
      await brandingApi.uploadHeader(config.type, preview);
      onSaved();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    setRemoving(true);
    setError('');
    try {
      await brandingApi.removeHeader(config.type);
      setPreview('');
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Could not remove header.');
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

      {/* Card header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-navy/8 flex items-center justify-center flex-shrink-0">
            <Icon size={16} className="text-navy"/>
          </div>
          <div>
            <h3 className="font-semibold text-navy text-sm">{config.label}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{config.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Remove button — only when image is set */}
          {hasImage && !isDirty && (
            <button
              onClick={handleRemove}
              disabled={removing}
              title="Remove custom header — revert to default layout"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200
                         text-red-500 text-xs font-semibold hover:bg-red-50 disabled:opacity-50 transition">
              {removing
                ? <Loader2 size={12} className="animate-spin"/>
                : <Trash2 size={12}/>}
              Remove
            </button>
          )}

          {/* Upload / Save button */}
          <button
            onClick={isDirty ? handleUpload : () => fileRef.current?.click()}
            disabled={uploading || (isDirty && !preview)}
            className={clsx(
              'flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold',
              'disabled:opacity-50 transition shadow-sm',
              isDirty
                ? 'bg-orange text-gray-900 hover:bg-orange-dark'
                : 'bg-navy/8 text-navy hover:bg-navy/12',
            )}>
            {uploading ? (
              <Loader2 size={12} className="animate-spin"/>
            ) : saved ? (
              <CheckCircle2 size={12} className="text-green-600"/>
            ) : isDirty ? (
              <Upload size={12}/>
            ) : (
              <Upload size={12}/>
            )}
            {saved ? 'Saved!' : isDirty ? 'Save Header' : 'Upload Image'}
          </button>
        </div>
      </div>

      {/* Preview / drop zone */}
      <div className="p-6 space-y-4">
        <div
          onClick={() => { if (!hasImage || isDirty) fileRef.current?.click(); }}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          className={clsx(
            'relative w-full rounded-xl border-2 border-dashed transition-colors overflow-hidden',
            !hasImage
              ? 'border-gray-200 hover:border-orange/50 hover:bg-orange/3 cursor-pointer'
              : 'border-gray-200 cursor-default',
          )}
          style={{ minHeight: '80px' }}
        >
          {preview ? (
            /* Header image preview */
            <div className="relative group">
              <img
                src={preview}
                alt={`${config.type} header preview`}
                className="w-full object-contain block"
                style={{ maxHeight: '100px' }}
              />
              {/* Overlay — change image */}
              <div
                onClick={e => { e.stopPropagation(); fileRef.current?.click(); }}
                className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all
                           flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer">
                <span className="bg-white/90 text-navy text-xs font-semibold px-3 py-1.5 rounded-lg shadow">
                  Change image
                </span>
              </div>
            </div>
          ) : (
            /* Empty state */
            <div className="flex flex-col items-center justify-center gap-2 py-8 text-gray-400">
              <ImageOff size={28} strokeWidth={1.5}/>
              <div className="text-center">
                <p className="text-sm font-medium">No custom header uploaded</p>
                <p className="text-xs mt-0.5">
                  Drag &amp; drop or click <span className="font-semibold text-navy">Upload Image</span> — PNG, JPEG, SVG, WebP · max 2 MB
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Status bar */}
        <div className="flex items-start justify-between gap-4">
          <div className="text-xs text-gray-400 space-y-0.5">
            {hasImage && !isDirty ? (
              <p className="flex items-center gap-1.5 text-green-600 font-medium">
                <CheckCircle2 size={11}/> Custom header active — using uploaded image on prints
              </p>
            ) : (
              <p className="flex items-center gap-1.5">
                <ImageOff size={11}/>
                Fallback active — prints show Hospital Logo + Name + Contact Info
              </p>
            )}
            <p>Recommended: <strong>full-width landscape</strong>, at least <strong>1200 × 200 px</strong>, max <strong>2 MB</strong></p>
          </div>
          {isDirty && (
            <button
              onClick={() => setPreview(currentUrl)}
              className="text-xs text-gray-400 hover:text-gray-600 underline flex-shrink-0">
              Discard changes
            </button>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
            <AlertCircle size={13} className="flex-shrink-0 mt-0.5"/>
            {error}
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        className="hidden"
        onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
      />
    </div>
  );
}

// ── TipTap toolbar button ─────────────────────────────────────────

function ToolbarBtn({
  onClick, active, title, children,
}: {
  onClick:  () => void;
  active?:  boolean;
  title:    string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      className={clsx(
        'p-1.5 rounded transition-colors',
        active
          ? 'bg-navy text-white'
          : 'text-gray-500 hover:bg-gray-100 hover:text-navy',
      )}
    >
      {children}
    </button>
  );
}

// ── Rich-text editor card for a single branding key ───────────────

interface HtmlEditorCardProps {
  label:       string;
  description: string;
  settingKey:  'prescription_custom_header_html' | 'prescription_footer_html' | 'receipt_custom_header_html' | 'receipt_footer_html';
  currentHtml: string;
  onSaved:     () => void;
}

function HtmlEditorCard({ label, description, settingKey, currentHtml, onSaved }: HtmlEditorCardProps) {
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState('');
  const [isDirty, setDirty] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: currentHtml || '',
    onUpdate: () => { setDirty(true); setSaved(false); },
  });

  // Sync when branding refreshes from server
  useEffect(() => {
    if (editor && !isDirty) {
      editor.commands.setContent(currentHtml || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHtml]);

  const handleSave = useCallback(async () => {
    if (!editor) return;
    setSaving(true);
    setError('');
    try {
      const html = editor.getHTML();
      // Treat empty-paragraph output as empty string
      const value = html === '<p></p>' ? '' : html;
      await brandingApi.update({ [settingKey]: value } as any);
      setSaved(true);
      setDirty(false);
      onSaved();
      setTimeout(() => setSaved(false), 2500);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editor, settingKey, onSaved]);

  const handleClear = useCallback(async () => {
    if (!editor) return;
    editor.commands.clearContent();
    setSaving(true);
    setError('');
    try {
      await brandingApi.update({ [settingKey]: '' } as any);
      setSaved(true);
      setDirty(false);
      onSaved();
      setTimeout(() => setSaved(false), 2500);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Clear failed.');
    } finally {
      setSaving(false);
    }
  }, [editor, settingKey, onSaved]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Card header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h3 className="font-semibold text-navy text-sm">{label}</h3>
          <p className="text-xs text-gray-400 mt-0.5">{description}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {saving && <Loader2 size={14} className="animate-spin text-gray-400"/>}
          {saved  && <CheckCircle2 size={14} className="text-green-500"/>}
          <button
            type="button"
            onClick={handleClear}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200
                       text-gray-500 text-xs font-medium hover:bg-gray-50 disabled:opacity-50 transition"
          >
            <Trash2 size={11}/> Clear
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold
                       bg-orange text-navy hover:bg-orange-dark disabled:opacity-50 transition shadow-sm"
          >
            <Upload size={11}/> {saved ? 'Saved!' : 'Save'}
          </button>
        </div>
      </div>

      <div className="p-4 space-y-2">
        {/* Formatting toolbar */}
        {editor && (
          <div className="flex items-center gap-0.5 px-1 py-1 bg-gray-50 rounded-lg border border-gray-200 w-fit">
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleBold().run()}
              active={editor.isActive('bold')}
              title="Bold"
            >
              <Bold size={13}/>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleItalic().run()}
              active={editor.isActive('italic')}
              title="Italic"
            >
              <Italic size={13}/>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              active={editor.isActive('underline')}
              title="Underline"
            >
              <UnderlineIcon size={13}/>
            </ToolbarBtn>
            <div className="w-px h-4 bg-gray-300 mx-1"/>
            <ToolbarBtn
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              active={editor.isActive({ textAlign: 'left' })}
              title="Align left"
            >
              <AlignLeft size={13}/>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              active={editor.isActive({ textAlign: 'center' })}
              title="Align center"
            >
              <AlignCenter size={13}/>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              active={editor.isActive({ textAlign: 'right' })}
              title="Align right"
            >
              <AlignRight size={13}/>
            </ToolbarBtn>
          </div>
        )}

        {/* Editor area */}
        <div className="border border-gray-200 rounded-lg min-h-[80px] px-3 py-2 text-sm
                        focus-within:ring-2 focus-within:ring-orange/30 focus-within:border-orange
                        [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[60px]
                        [&_.ProseMirror_p]:m-0 [&_.ProseMirror_p:not(:last-child)]:mb-1">
          <EditorContent editor={editor}/>
        </div>

        <p className="text-xs text-gray-400">
          Output is saved as HTML and rendered directly in print layouts. Use the toolbar to format text.
        </p>

        {error && (
          <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
            <AlertCircle size={13} className="flex-shrink-0 mt-0.5"/>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Doctor block toggle ────────────────────────────────────────────
// Controls whether the system renders a standalone doctor credentials
// block (name, qualifications, Reg. No.) on the Rx printout.
// Set to ON when the pre-printed letterhead already carries this info.
function DoctorBlockToggle({
  currentValue,
  onSaved,
}: {
  currentValue: boolean;
  onSaved:      () => void;
}) {
  const [value,   setValue]   = useState(currentValue);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => { setValue(currentValue); }, [currentValue]);

  async function handleToggle(next: boolean) {
    setValue(next);
    setSaved(false);
    setError('');
    setSaving(true);
    try {
      await brandingApi.update({ doctor_details_in_header: next ? '1' : '' } as any);
      setSaved(true);
      onSaved();
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError('Failed to save. Please try again.');
      setValue(!next);  // revert on error
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100">
        <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
          <UserCheck size={15} className="text-violet-600"/>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-navy leading-tight">
            Doctor Credentials on Prescription
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            Controls whether the system prints the doctor's name, qualifications, and Reg. No. below the header.
          </p>
        </div>
        {saving && <Loader2 size={14} className="animate-spin text-gray-400 flex-shrink-0"/>}
        {saved  && <CheckCircle2 size={14} className="text-green-500 flex-shrink-0"/>}
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Toggle row */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-700">
              Doctor details already on pre-printed letterhead
            </p>
            <p className="text-xs text-gray-400 leading-relaxed">
              Turn this <strong>ON</strong> if your printed stationery (or header image above) already
              shows the consulting doctor's name, degree, and registration number.
              The system will then skip rendering a duplicate credentials block on each Rx.
              <br/>
              Turn this <strong>OFF</strong> to have the system automatically print the doctor's
              credentials below the header — required when using plain paper or a generic letterhead.
            </p>
          </div>
          {/* Toggle switch */}
          <button
            onClick={() => handleToggle(!value)}
            disabled={saving}
            aria-pressed={value}
            className={clsx(
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent',
              'transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2',
              'disabled:opacity-60 disabled:cursor-not-allowed',
              value ? 'bg-violet-600' : 'bg-gray-200',
            )}>
            <span className={clsx(
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0',
              'transition duration-200 ease-in-out',
              value ? 'translate-x-5' : 'translate-x-0',
            )}/>
          </button>
        </div>

        {/* Status chip */}
        <div className={clsx(
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium',
          value
            ? 'bg-violet-50 text-violet-700 border border-violet-200'
            : 'bg-amber-50 text-amber-700 border border-amber-200',
        )}>
          {value ? (
            <><CheckCircle2 size={11}/> Credentials block hidden — letterhead carries doctor info</>
          ) : (
            <><UserCheck size={11}/> Credentials block active — system prints doctor name + Reg. No.</>
          )}
        </div>

        {error && (
          <p className="text-xs text-red-500 flex items-center gap-1.5">
            <AlertCircle size={11}/>{error}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────
export default function PrintSettings() {
  const queryClient = useQueryClient();
  const user        = useAuthStore(s => s.user);
  const { data: branding, isLoading } = useBranding();

  function onSaved() {
    queryClient.invalidateQueries({ queryKey: ['branding'] });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 size={22} className="animate-spin"/>
      </div>
    );
  }

  const settings = branding ?? ({} as Branding);

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">

      {/* Page header */}
      <div>
        <h1 className="text-lg font-bold text-navy">Document Print Headers</h1>
        <p className="text-xs text-gray-400 mt-0.5">
          Upload a custom full-width header image for each document type.
          If no image is set, prints automatically fall back to&nbsp;
          <span className="font-medium text-navy">
            {user?.hospitalName ?? 'your hospital'}'s
          </span>
          &nbsp;logo, name, and contact info.
        </p>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700">
        <AlertCircle size={14} className="flex-shrink-0 mt-0.5 text-blue-500"/>
        <div>
          <p className="font-semibold mb-0.5">How this works</p>
          <p>
            Each card controls a specific document type. Upload a PNG or JPEG of your hospital
            letterhead (logo already pre-printed in the image). When a custom header is active,
            it replaces the auto-generated logo + contact block — the doctor's name and patient
            info still appear below it automatically.
          </p>
        </div>
      </div>

      {/* ── Doctor credentials toggle ─────────────────────────── */}
      <DoctorBlockToggle
        currentValue={settings.doctor_details_in_header ?? false}
        onSaved={onSaved}
      />

      {/* ── One card per document type ───────────────────────── */}
      {DOC_CONFIGS.map(config => (
        <HeaderCard
          key={config.type}
          config={config}
          currentUrl={(settings[config.brandingKey] as string) ?? ''}
          onSaved={onSaved}
        />
      ))}

      {/* ── Rich-text HTML blocks ─────────────────────────────── */}
      <div className="pt-4 border-t border-gray-100">
        <h2 className="text-sm font-bold text-navy mb-1">Custom Prescription Content</h2>
        <p className="text-xs text-gray-400 mb-4">
          Rich-text blocks rendered inside prescription print layouts below the header image
          and above the writing area. Used for doctor schedules, custom disclaimers, or
          additional hospital branding text.
        </p>

        <div className="space-y-4">
          <HtmlEditorCard
            label="Prescription — Custom Header Text"
            description="Displayed below the header image on every blank prescription print."
            settingKey="prescription_custom_header_html"
            currentHtml={settings.prescription_custom_header_html ?? ''}
            onSaved={onSaved}
          />
          <HtmlEditorCard
            label="Prescription — Footer Text"
            description="Overrides the default address/phone footer on prescription prints."
            settingKey="prescription_footer_html"
            currentHtml={settings.prescription_footer_html ?? ''}
            onSaved={onSaved}
          />
          <HtmlEditorCard
            label="Receipt / Invoice — Custom Header Text"
            description="Displayed below the header image on billing invoices and receipts."
            settingKey="receipt_custom_header_html"
            currentHtml={settings.receipt_custom_header_html ?? ''}
            onSaved={onSaved}
          />
          <HtmlEditorCard
            label="Receipt / Invoice — Footer Text"
            description="Overrides the default footer on billing invoices and receipts."
            settingKey="receipt_footer_html"
            currentHtml={settings.receipt_footer_html ?? ''}
            onSaved={onSaved}
          />
        </div>
      </div>

    </div>
  );
}
